import { useEffect, useState } from "react";
import { api } from "../lib/api.js";

interface Guest {
  id: string;
  name: string;
  email: string;
  title?: string;
  company?: string;
  tags: string[];
}

interface SuggestedGuest {
  guest: Guest;
  score: {
    total: number;
    tagScore: number;
    performanceScore: number;
    availabilityScore: number;
    breakdown: string[];
  };
}

interface Props {
  episodeId: string;
}

export function SuggestedGuests({ episodeId }: Props) {
  const [suggestions, setSuggestions] = useState<SuggestedGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSuggestions() {
      try {
        setLoading(true);
        const response = await api.get(`/api/episodes/${episodeId}/suggested-guests`);
        
        if (!response.ok) {
          throw new Error("Failed to fetch suggestions");
        }
        
        const data = await response.json();
        setSuggestions(data.suggestedGuests || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load suggestions");
      } finally {
        setLoading(false);
      }
    }

    fetchSuggestions();
  }, [episodeId]);

  if (loading) {
    return (
      <div className="border rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4">AI Suggested Guests</h3>
        <div className="flex justify-center py-4">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4">AI Suggested Guests</h3>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="border rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4">AI Suggested Guests</h3>
        <p className="text-sm text-muted-foreground">
          No suggestions available. Add guest expertise tags and episode topics to get better matches.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6">
      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
        AI Suggested Guests
        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
          {suggestions.length} matches
        </span>
      </h3>
      
      <div className="space-y-3">
        {suggestions.map((suggestion, index) => (
          <div
            key={suggestion.guest.id}
            className="p-3 border rounded-md hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{suggestion.guest.name}</span>
                  {index === 0 && (
                    <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                      Top Pick
                    </span>
                  )}
                </div>
                {(suggestion.guest.title || suggestion.guest.company) && (
                  <p className="text-sm text-muted-foreground">
                    {suggestion.guest.title}
                    {suggestion.guest.title && suggestion.guest.company && " at "}
                    {suggestion.guest.company}
                  </p>
                )}
              </div>
              
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-1.5">
                  <div className="text-2xl font-bold text-primary">
                    {suggestion.score.total}
                  </div>
                  <div className="text-xs text-muted-foreground">/100</div>
                </div>
              </div>
            </div>
            
            {/* Score breakdown */}
            <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
              <div>
                <span className="text-muted-foreground">Tags:</span>
                <span className="ml-1 font-medium">{suggestion.score.tagScore}/40</span>
              </div>
              <div>
                <span className="text-muted-foreground">Performance:</span>
                <span className="ml-1 font-medium">{suggestion.score.performanceScore}/40</span>
              </div>
              <div>
                <span className="text-muted-foreground">Availability:</span>
                <span className="ml-1 font-medium">{suggestion.score.availabilityScore}/20</span>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-secondary rounded-full h-2 mb-2">
              <div
                className="bg-primary rounded-full h-2 transition-all"
                style={{ width: `${suggestion.score.total}%` }}
              />
            </div>
            
            {/* Breakdown details */}
            <div className="text-xs text-muted-foreground space-y-0.5">
              {suggestion.score.breakdown.map((item, i) => (
                <div key={i}>• {item}</div>
              ))}
            </div>
            
            {/* Tags */}
            {suggestion.guest.tags && suggestion.guest.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {suggestion.guest.tags.slice(0, 5).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-xs bg-secondary rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {suggestion.guest.tags.length > 5 && (
                  <span className="text-xs text-muted-foreground">
                    +{suggestion.guest.tags.length - 5} more
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <p className="text-xs text-muted-foreground mt-3">
        Suggestions based on topic matching, past performance, and availability
      </p>
    </div>
  );
}
